"use client";

import type { LucideIcon } from "lucide-react";
import {
  Eye,
  Layers,
  LayoutGrid,
  Menu,
  MessageSquare,
  Plug,
  Search,
  User
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";

import { useAuth } from "@/components/auth/auth-provider";
import { isDevEnvironment } from "@/lib/env";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

// `hidden: true` keeps the entry in code but excludes it from the nav (route still works directly).
// `devOnly: true` hides the entry on prod but shows it on dev.
// `adminOnly: true` shows the entry only to admins, on any env (pairs with AdminOnlyRoute).
// `authOnly: true` shows the entry only to signed-in users.
// `mitonOnly: true` shows the entry only to Miton people (role + @miton.cz, decided by the API).
// `flagship: true` renders with the prominent blue MCP pill styling.
const NAV_LINKS: {
  label: string;
  href: string;
  icon: LucideIcon;
  devOnly?: boolean;
  adminOnly?: boolean;
  authOnly?: boolean;
  /** Only Miton people (the API's `miton_access`). */
  mitonOnly?: boolean;
  hidden?: boolean;
  flagship?: boolean;
}[] = [
  { label: "Agentic Search", href: "/recruiter", icon: MessageSquare, hidden: true },
  { label: "Simple Search", href: "/search", icon: Search, adminOnly: true },
  { label: "MCP", href: "/settings", icon: Plug, flagship: true },
  { label: "Profile Lookup", href: "/lookup", icon: User, devOnly: true },
  { label: "Stacks", href: "/stacks", icon: Layers, devOnly: true },
  { label: "Miton Talent", href: "/miton", icon: Eye, mitonOnly: true },
  { label: "Projects", href: "/projects", icon: LayoutGrid, hidden: true }
];

function navLinkStyle(isActive: boolean): React.CSSProperties {
  return {
    fontSize: "13px",
    fontWeight: 500,
    color: isActive ? "rgba(0,0,0,0.85)" : "rgba(0,0,0,0.4)",
    textDecoration: "none",
    display: "flex",
    alignItems: "center",
    gap: "5px",
    transition: "color 0.2s"
  };
}

export function TopHeader() {
  const pathname = usePathname();
  const router = useRouter();
  const { user, isAdmin, isMiton, logout } = useAuth();
  // Defer dev-only filter until after mount: `isDevEnvironment()` returns
  // false on the server (no window) and true on dev hostname client-side.
  // Without this gate, the SSR HTML omits dev-only links and the client
  // adds them, producing a hydration mismatch.
  const [isDev, setIsDev] = useState(false);
  useEffect(() => { setIsDev(isDevEnvironment()); }, []);
  const visibleLinks = NAV_LINKS.filter(
    (link) => !link.hidden && (!link.devOnly || isDev) && (!link.adminOnly || isAdmin) && (!link.authOnly || Boolean(user)) && (!link.mitonOnly || isMiton)
  );

  // Build login URL that returns user to their current page after sign-in
  const getLoginHref = () => {
    const currentUrl = typeof window !== "undefined"
      ? window.location.pathname + window.location.search
      : pathname;
    return `/login?next=${encodeURIComponent(currentUrl)}`;
  };

  return (
    <header
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 40,
        borderBottom: "1px solid var(--border)",
        background: "rgba(232,227,217,0.88)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)"
      }}
    >
      <div
        className="page-container"
        style={{
          height: "var(--nav-h)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between"
        }}
      >
        <Link
          href="/"
          style={{
            border: "none",
            background: "transparent",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            cursor: "pointer",
            textDecoration: "none"
          }}
        >
          <Image
            src="/branding/alister-logo-symbol.svg"
            alt="Alister logo"
            width={32}
            height={32}
            priority
          />
          <span
            style={{
              color: "var(--black)",
              fontSize: "0.86rem",
              letterSpacing: "0.1em",
              fontWeight: 700
            }}
          >
            ALISTER AI
          </span>
          {isDev && <span className="env-badge env-dev-only">Dev</span>}
        </Link>

        <nav style={{ display: "flex", alignItems: "center", gap: "32px" }}>
          {visibleLinks.map((link) => {
            const isActive = pathname.startsWith(link.href);
            const Icon = link.icon;
            // Combine flagship pill styling with `.env-dev-only` so the
            // admin env-preview toggle can hide dev-only items via CSS.
            const className = [
              link.flagship ? "nav-link-mcp" : null,
              link.devOnly ? "env-dev-only" : null,
            ].filter(Boolean).join(" ") || undefined;
            return user ? (
              <Link
                key={link.href}
                href={link.href}
                className={className}
                style={navLinkStyle(isActive)}
              >
                <Icon size={13} style={{ opacity: isActive ? 1 : 0.55, flexShrink: 0, transition: "opacity 0.2s" }} />
                {link.label}
              </Link>
            ) : (
              <button
                key={link.href}
                type="button"
                onClick={() => router.push(getLoginHref())}
                className={className}
                style={{
                  ...navLinkStyle(false),
                  border: "none",
                  background: "transparent",
                  cursor: "pointer"
                }}
              >
                <Icon size={13} style={{ opacity: 0.55, flexShrink: 0 }} />
                {link.label}
              </button>
            );
          })}
        </nav>

        <div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                aria-label="Open main navigation"
                type="button"
                style={{
                  border: "1px solid var(--border)",
                  background: "var(--dim)",
                  color: "var(--muted)",
                  width: "36px",
                  height: "36px",
                  borderRadius: "var(--r-sm)",
                  display: "grid",
                  placeItems: "center",
                  cursor: "pointer",
                  transition: "all 0.2s"
                }}
              >
                <Menu size={16} />
              </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end">
              {user ? (
                <>
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link href="/admin">Admin</Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem asChild>
                    <Link href="/settings">Settings</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => logout()}>
                    Sign out
                  </DropdownMenuItem>
                  <div
                    style={{
                      borderTop: "1px solid var(--border)",
                      padding: "0.4rem 0.5rem 0.25rem",
                      fontSize: "0.7rem",
                      color: "var(--text-muted)",
                      letterSpacing: "0.02em",
                      pointerEvents: "none",
                      userSelect: "none",
                    }}
                  >
                    {user.email}
                  </div>
                </>
              ) : (
                <DropdownMenuItem asChild>
                  <Link href={getLoginHref()}>Sign in</Link>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
